import { useState, useEffect, useRef } from 'react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../context/AdminAuthContext';
import { IconEdit, IconTrash, IconUpload, IconCamera, IconMicSmall, IconPin, IconCalendar } from '../components/Icons';
import './Admin.css';

const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 600;
        const MAX_HEIGHT = 400;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
        resolve(dataUrl);
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const defaultForm = { title: '', location: '', date: '', description: '', youtubeUrl: '', thumbnailUrl: '' };

export default function AdminSpeaking() {
  const { authFetch } = useAdminAuth();
  const [events,   setEvents]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [dragging, setDragging] = useState(false);
  const [form,     setForm]     = useState(defaultForm);
  const [editId,   setEditId]   = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const fileInputRef = useRef();
  const thumbInputRef = useRef();

  const handleThumbFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await compressImage(file);
      setForm(f => ({ ...f, thumbnailUrl: base64 }));
    } catch (err) {
      console.error("Failed to compress thumbnail:", err);
      setError("Failed to process image.");
    }
  };

  const loadEvents = () => {
    setLoading(true);
    fetch('/api/speaking')
      .then(r => {
        if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
        return r.json();
      })
      .then(data => { 
        let loadedEvents = [];
        if (data && data.items) {
          loadedEvents = data.items;
        } else if (Array.isArray(data)) {
          loadedEvents = data;
        }

        const parseEventDate = (evt) => {
          if (!evt || !evt.date) return 0;
          const dateStr = String(evt.date).trim();
          const parsed = Date.parse(dateStr);
          if (!isNaN(parsed)) return parsed;
          const yearMatch = dateStr.match(/\b(19|20)\d{2}\b/);
          if (yearMatch) {
            const year = parseInt(yearMatch[0], 10);
            const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
            const lower = dateStr.toLowerCase();
            for (let i = 0; i < 12; i++) {
              if (lower.includes(months[i])) {
                return new Date(year, i, 1).getTime();
              }
            }
            return new Date(year, 0, 1).getTime();
          }
          return evt.id || 0;
        };

        loadedEvents.sort((a, b) => parseEventDate(b) - parseEventDate(a));
        setEvents(loadedEvents);
        setLoading(false); 
      })
      .catch((err) => {
        console.error("Fetch speaking error in admin:", err);
        setEvents([]);
        setLoading(false);
      });
  };

  useEffect(() => { loadEvents(); }, []);

  const handleFiles = (files) => {
    if (!files || files.length === 0) return;
    window._pendingSpeakingFiles = Array.from(files);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    
    const fd = new FormData();
    fd.append('title',       form.title || '');
    fd.append('location',    form.location || '');
    fd.append('date',        form.date || '');
    fd.append('description', form.description || '');
    fd.append('youtubeUrl',  form.youtubeUrl || '');
    fd.append('thumbnailUrl', form.thumbnailUrl || '');
    
    const pendingFiles = window._pendingSpeakingFiles;
    if (pendingFiles) {
      pendingFiles.forEach(file => fd.append('images[]', file));
    }

    try {
      let res;
      if (editId) {
        // For editing, if no new files, we can just send JSON, 
        // but since the endpoint handles multipart/form-data for uploads, we use FormData.
        // PHP handles multipart/form-data best via POST.
        res = await authFetch(`/api/speaking/${editId}`, { method: 'POST', body: fd });
      } else {
        res = await authFetch('/api/speaking', { method: 'POST', body: fd });
      }

      if (!res.ok) throw new Error('Action failed');
      
      window._pendingSpeakingFiles = null;
      setShowModal(false);
      loadEvents();
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const openNew = () => {
    setEditId(null);
    setForm(defaultForm);
    window._pendingSpeakingFiles = null;
    setShowModal(true);
  };

  const openEdit = (event) => {
    setEditId(event.id);
    setForm({ 
      title: event.title, 
      location: event.location, 
      date: event.date, 
      description: event.description, 
      youtubeUrl: event.youtubeUrl,
      thumbnailUrl: event.thumbnailUrl || ''
    });
    window._pendingSpeakingFiles = null;
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this event?')) return;
    await authFetch(`/api/speaking/${id}`, { method: 'DELETE' });
    loadEvents();
  };

  return (
    <AdminLayout title="Speaking Engagements" page="speaking">
      <div className="admin-content">
        <div className="admin-page-header">
          <h2>Speaking Engagements & Conferences</h2>
          <button className="btn btn-primary btn-sm" onClick={openNew}>Add New Event</button>
        </div>

        {loading ? (
          <div className="admin-loading">Loading events…</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Event Details</th>
                  <th>Location & Date</th>
                  <th>Media</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map(event => (
                  <tr key={event.id}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--navy)', marginBottom: '4px' }}>{event.title}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', maxHeight: '3em', overflow: 'hidden' }}>{event.description}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '4px' }}><IconPin size={14} /> {event.location}</div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-light)', display: 'flex', alignItems: 'center', gap: '4px' }}><IconCalendar size={14} /> {event.date}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {event.youtubeUrl && <span className="badge badge-navy" style={{ fontSize: '0.7rem' }}>Video</span>}
                        {event.images && event.images.length > 0 && <span className="badge badge-gold" style={{ fontSize: '0.7rem' }}>{event.images.length} Image(s)</span>}
                      </div>
                    </td>
                    <td>
                      <div className="admin-table-actions">
                        <button className="btn btn-outline btn-sm" onClick={() => openEdit(event)} title="Edit"><IconEdit size={14} /></button>
                        <button className="btn btn-danger btn-sm"  onClick={() => handleDelete(event.id)} title="Delete"><IconTrash size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="admin-modal-backdrop">
          <div className="admin-modal" style={{ maxWidth: '600px' }}>
            <h3>{editId ? 'Edit Event' : 'Add New Event'}</h3>
            <div className="admin-form">
              <div className="admin-field">
                <label>Event Title</label>
                <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="e.g. UN Human Rights Council side event" />
              </div>
              <div className="admin-form-row">
                <div className="admin-field">
                  <label>Location</label>
                  <input value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} placeholder="City, Country" />
                </div>
                <div className="admin-field">
                  <label>Date</label>
                  <input value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} placeholder="March 2024" />
                </div>
              </div>
              <div className="admin-field">
                <label>Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={4} placeholder="Summary of the talk or engagement..." />
              </div>
               <div className="admin-field">
                <label>YouTube Link (Optional)</label>
                <input value={form.youtubeUrl} onChange={e => setForm(f => ({...f, youtubeUrl: e.target.value}))} placeholder="https://www.youtube.com/watch?v=..." />
              </div>
              
              <div className="admin-field">
                <label>Custom Thumbnail (Optional)</label>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '0.5rem' }}>
                  {form.thumbnailUrl ? (
                    <div style={{ position: 'relative', width: '120px', height: '80px', borderRadius: '6px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                      <img src={form.thumbnailUrl} alt="Thumbnail Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button 
                        type="button"
                        onClick={() => setForm(f => ({ ...f, thumbnailUrl: '' }))}
                        style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '10px' }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => thumbInputRef.current?.click()}
                      style={{ width: '120px', height: '80px', borderRadius: '6px', border: '1px dashed var(--border)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'var(--bg-input)' }}
                    >
                      <IconCamera size={18} color="var(--text-light)" />
                      <span style={{ fontSize: '0.65rem', color: 'var(--text-light)', marginTop: '4px' }}>Upload Thumb</span>
                    </div>
                  )}
                  <input ref={thumbInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleThumbFile} />
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', flex: 1 }}>
                    If no custom thumbnail is uploaded, the YouTube video preview (or event gallery image) will be used automatically.
                  </div>
                </div>
              </div>
              
              <div className="admin-field">
                <label>Gallery Images (Optional)</label>
                <div 
                  className={`admin-upload-zone ${dragging ? 'admin-upload-zone--drag' : ''}`}
                  onDragOver={e => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
                  onClick={() => fileInputRef.current?.click()}
                  style={{ padding: '1rem', minHeight: 'auto' }}
                >
                  <IconUpload size={24} />
                  <span style={{ fontSize: '0.85rem' }}>Click or drag images to upload</span>
                </div>
                <input ref={fileInputRef} type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
                {window._pendingSpeakingFiles && (
                  <p style={{ fontSize: '0.8rem', color: 'var(--gold)', marginTop: '0.5rem' }}>
                    {window._pendingSpeakingFiles.length} new image(s) staged.
                  </p>
                )}
              </div>

              {error && <p style={{ color: '#dc2626', fontSize: '0.82rem' }}>{error}</p>}
            </div>
            <div className="admin-modal-footer">
              <button className="btn btn-outline btn-sm" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : (editId ? 'Save Changes' : 'Create Event')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
